// Manually file a PR under a ticket key — the escape hatch for when auto-
// adoption mis-keyed a PR (e.g. CRM-651's PRs rolled up to the CRM-1 Epic) or
// never picked it up. The key can be an existing Jira ticket (CRM-651) or a
// QA-invented group (REL-1). Keeps the key after a successful assign so QA can
// add several PRs (api + client) to the same group in a row.

import { useCallback, useEffect, useState } from "react";
import api from "../../api";
import Icon from "./icons";
import { Modal, Spinner, apiErrorMessage } from "./shared";

const ISSUE_KEY_RE = /^[A-Z][A-Z0-9]+-\d+$/;

function resultMessage(r) {
  const pr = `${r.repoId} #${r.prNumber}`;
  if (r.action === "moved") return `Moved ${pr} from ${r.from} → ${r.key}.`;
  if (r.action === "unchanged") return `${pr} was already under ${r.key}.`;
  return `Assigned ${pr} to ${r.key}.`;
}

export default function AssignPrModal({ open, onClose, onAssigned, initialKey }) {
  const [key, setKey] = useState("");
  const [pr, setPr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setKey(initialKey || "");
    setPr("");
    setSubmitting(false);
    setError(null);
    setResult(null);
  }, [open, initialKey]);

  const keyValid = ISSUE_KEY_RE.test(key.trim());
  const prValid = pr.trim().length > 0;

  const submit = useCallback(() => {
    if (!keyValid || !prValid || submitting) return;
    setSubmitting(true);
    setError(null);
    api
      .bssAssignPr(key.trim(), pr.trim())
      .then((res) => {
        setSubmitting(false);
        setResult(res.data);
        setPr(""); // keep the key so QA can add the next PR to the same group
        onAssigned && onAssigned(res.data);
      })
      .catch((err) => {
        setSubmitting(false);
        setResult(null);
        setError(apiErrorMessage(err, "Could not assign the PR."));
      });
  }, [key, pr, keyValid, prValid, submitting, onAssigned]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Assign a PR to a ticket"
      width={560}
      footer={
        <>
          <span style={{ marginRight: "auto", fontSize: 11.5, color: "var(--bss-fg-subtle)" }}>
            Files the PR under the ticket — then find it by key and deploy.
          </span>
          <button type="button" className="bss-btn bss-btn--ghost" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="bss-btn bss-btn--primary"
            disabled={!keyValid || !prValid || submitting}
            onClick={submit}
          >
            {submitting ? (
              <>
                <Spinner /> Assigning…
              </>
            ) : (
              <>
                <Icon.Plus size={13} /> Assign PR
              </>
            )}
          </button>
        </>
      }
    >
      <div className="bss-form-row">
        <label className="bss-form-label" htmlFor="assign-key">
          Ticket key
        </label>
        <input
          id="assign-key"
          className="bss-input"
          style={{ width: "100%", fontFamily: "var(--bss-font-mono)" }}
          placeholder="CRM-651 (existing) or REL-1 (new group)"
          value={key}
          onChange={(e) => setKey(e.target.value.toUpperCase())}
        />
        {key && !keyValid && (
          <div className="bss-form-hint" style={{ color: "var(--bss-danger)" }}>
            Format: ABC-123
          </div>
        )}
        <div className="bss-form-hint">
          An existing Jira ticket, or invent a group key (e.g. REL-1) to deploy a set together.
        </div>
      </div>

      <div className="bss-form-row">
        <label className="bss-form-label" htmlFor="assign-pr">
          Pull request
        </label>
        <input
          id="assign-pr"
          className="bss-input"
          style={{ width: "100%" }}
          placeholder="https://github.com/boompay/boompay-api/pull/4225"
          value={pr}
          onChange={(e) => setPr(e.target.value)}
        />
        <div className="bss-form-hint">Paste the GitHub PR URL (or owner/repo#number).</div>
      </div>

      {result && (
        <div className="bss-form-row" style={{ fontSize: 12.5, color: "var(--bss-success)" }}>
          {resultMessage(result)} Paste another PR for the same key, or Close.
        </div>
      )}
      {error && (
        <div className="bss-form-row" style={{ fontSize: 12.5, color: "var(--bss-danger)" }}>
          {error}
        </div>
      )}
    </Modal>
  );
}
